import React, { useEffect, useMemo, useState } from 'react'

import FiatSelectionPill from '@src/pages/FiatExchange/components/FiatSelectionPill'
import TokenSelectionPill from '@src/pages/FiatExchange/components/TokenSelectionPill'
import { DEFAULT_ONRAMP_FIAT_CURRENCY, DEFAULT_ONRAMP_TOKEN } from '@src/pages/FiatExchange/config'
import { PaymentMethod } from '@src/types/coinbase'
import { FiatCurrency } from '@src/types/fiat'
import { IToken } from '@src/types/token'
import { createContextLogger } from '@src/utils'
import { useConfigStore } from '@src/zustand_stores'

const logger = createContextLogger('SellSection')

export interface SellSectionProps {
    /**
     * Current value as a string, since it comes from input
     */
    value: string

    /**
     * Placeholder text for input
     */
    placeholder?: string

    /**
     * Value change handler to notify parent
     */
    onInputValueChange: (value: string) => void

    /** Notify parent component when fiat currency changes (for onramp) */
    onCurrencyChanged?: (newCurrency: FiatCurrency) => void

    /** Notify parent component when token changes (for offramp) */
    onTokenChanged?: (newToken: IToken) => void

    /** Whether this is in offramp mode (sell token instead of fiat) */
    isOfframp?: boolean

    /** Currently selected payment method (if any) */
    selectedPaymentMethod?: PaymentMethod
}

const SellSection: React.FC<SellSectionProps> = ({
    value,
    placeholder = '0',
    onInputValueChange = (value: string) => {},
    onCurrencyChanged,
    onTokenChanged,
    isOfframp = false,
    selectedPaymentMethod,
}) => {
    const availableNetworks = useConfigStore(config => config.availableNetworks)
    // Initialize with the default fiat currency or token based on mode.
    const [selectedFiat, setSelectedFiat] = useState<FiatCurrency>(DEFAULT_ONRAMP_FIAT_CURRENCY)
    const [selectedToken, setSelectedToken] = useState<IToken>(DEFAULT_ONRAMP_TOKEN)

    // When a new fiat is selected, update local state and notify parent.
    const handleFiatChange = (fiat?: FiatCurrency) => {
        if (fiat && onCurrencyChanged) {
            setSelectedFiat(fiat)
            onCurrencyChanged(fiat)
            logger.info('Onramp fiat currency changed', fiat)
        }
    }

    // Handle token change for offramp mode
    const handleTokenChange = (newData: { chainId?: number; token?: IToken }) => {
        if (newData.token && onTokenChanged) {
            setSelectedToken(newData.token)
            onTokenChanged(newData.token)
            logger.info('Offramp token changed to', newData.token)
        } else {
            logger.warn('No token provided in token change')
        }
    }

    // State for validation error message
    const [errorMessage, setErrorMessage] = useState<string>('')

    // Validate value against payment method limits
    useEffect(() => {
        // Clear error if no payment method is selected or no value entered
        if (!selectedPaymentMethod || !value || value === '0') {
            setErrorMessage('')
            return
        }

        const numericValue = parseFloat(value)
        // Skip validation for invalid numbers
        if (isNaN(numericValue)) {
            setErrorMessage('')
            return
        }

        // Check if payment method has limits
        if (selectedPaymentMethod.limits && selectedPaymentMethod.limits.length > 0) {
            const { min, max } = selectedPaymentMethod.limits[0]

            // Determine the appropriate symbol to display
            const currencySymbol = isOfframp
                ? selectedToken.symbol || selectedPaymentMethod.limits[0].currency
                : selectedFiat.symbol || selectedPaymentMethod.limits[0].currency

            if (min && numericValue < parseFloat(min)) {
                setErrorMessage(`Minimum amount is ${min} ${currencySymbol}`)
                return
            }

            if (max && numericValue > parseFloat(max)) {
                setErrorMessage(`Maximum amount is ${max} ${currencySymbol}`)
                return
            }

            // Clear error if value is within limits
            setErrorMessage('')
        }
    }, [value, selectedPaymentMethod, isOfframp, selectedFiat, selectedToken])

    // Handler for input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onInputValueChange(e.target.value)
    }

    return (
        <div className='space-y-4 rounded-3xl border-mach-surface-border bg-mach-surface-background p-5'>
            {/* Clear floats so that this row starts below the floated element */}
            <div className='clear-both flex items-center justify-between'>
                {/* Label row */}
                <div className='flex items-center gap-2'>
                    <span className='text-sm text-mach-accent-forgeround'>Sell</span>
                </div>

                {isOfframp ? (
                    <TokenSelectionPill
                        onChange={handleTokenChange}
                        token={selectedToken}
                        availableNetworks={availableNetworks}
                    />
                ) : (
                    <FiatSelectionPill fiat={selectedFiat} onChange={handleFiatChange} />
                )}
            </div>
            <div className='flex flex-col gap-1'>
                <div className='flex items-center justify-between'>
                    <input
                        className={`w-full bg-transparent text-left text-lg md:text-[32px] ${errorMessage ? 'text-mach-color-error' : ''}`}
                        placeholder={placeholder}
                        value={value}
                        onChange={handleInputChange}
                        inputMode='decimal'
                        required
                    />
                    <div className='relative'>{/* Additional UI can go here */}</div>
                </div>
                {/* Error message */}
                {errorMessage && <div className='text-mach-color-error text-sm'>{errorMessage}</div>}
            </div>
        </div>
    )
}

export default SellSection
